import React from 'react';
import { Calendar, ChevronRight, ChevronDown } from 'lucide-react';

interface MonthGroup {
  year: number;
  month: number;
  count: number;
  label: string;
}

interface EventTypeFilter {
  type: string;
  label: string;
  icon?: string;
  count: number;
  enabled: boolean;
}

interface SignificanceLevel {
  key: string;
  label: string;
  count: number;
  enabled: boolean;
}

interface ChronologySidebarProps {
  monthGroups: MonthGroup[];
  eventTypeFilters: EventTypeFilter[];
  onJumpToMonth: (year: number, month: number) => void;
  onFilterChange: (type: string, enabled: boolean) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isDateSort?: boolean;
  sortLabel?: string;
  significanceLevels?: SignificanceLevel[];
  onSignificanceChange?: (key: string, enabled: boolean) => void;
}

export const ChronologySidebar: React.FC<ChronologySidebarProps> = ({
  monthGroups,
  eventTypeFilters,
  onJumpToMonth,
  onFilterChange,
  isCollapsed,
  onToggleCollapse,
  isDateSort = true,
  sortLabel,
  significanceLevels,
  onSignificanceChange
}) => {
  const [expandedYears, setExpandedYears] = React.useState<Set<number>>(new Set());

  // Group months by year, then merge consecutive sparse years into ranges
  const SPARSE_THRESHOLD = 3; // years with ≤ this many events are candidates for merging

  type YearEntry = {
    year: number;
    yearEnd?: number;        // set when this is a merged range
    months: MonthGroup[];
    totalCount: number;
    isRange?: boolean;
  };

  const yearGroups = React.useMemo(() => {
    const groups = new Map<number, MonthGroup[]>();
    monthGroups.forEach(group => {
      if (!groups.has(group.year)) groups.set(group.year, []);
      groups.get(group.year)!.push(group);
    });

    // Sort years descending (most recent first)
    const sorted = Array.from(groups.entries())
      .sort(([a], [b]) => b - a)
      .map(([year, months]) => ({
        year,
        months: months.sort((a, b) => b.month - a.month),
        totalCount: months.reduce((sum, m) => sum + m.count, 0),
      }));

    // Merge consecutive sparse years into ranges
    const merged: YearEntry[] = [];
    let i = 0;
    while (i < sorted.length) {
      const entry = sorted[i];
      if (entry.totalCount <= SPARSE_THRESHOLD) {
        // Collect consecutive sparse entries
        const sparseRun: typeof sorted = [entry];
        let j = i + 1;
        while (j < sorted.length && sorted[j].totalCount <= SPARSE_THRESHOLD) {
          sparseRun.push(sorted[j]);
          j++;
        }
        if (sparseRun.length >= 2) {
          // Merge into a range (sparseRun is sorted desc, so first = newest, last = oldest)
          const allMonths = sparseRun.flatMap(s => s.months).sort((a, b) => b.year - a.year || b.month - a.month);
          merged.push({
            year: sparseRun[sparseRun.length - 1].year, // oldest
            yearEnd: sparseRun[0].year,                  // newest
            months: allMonths,
            totalCount: sparseRun.reduce((sum, s) => sum + s.totalCount, 0),
            isRange: true,
          });
          i = j;
        } else {
          merged.push(entry);
          i++;
        }
      } else {
        merged.push(entry);
        i++;
      }
    }
    return merged;
  }, [monthGroups]);

  const toggleYear = (year: number) => {
    const newExpanded = new Set(expandedYears);
    if (newExpanded.has(year)) {
      newExpanded.delete(year);
    } else {
      newExpanded.add(year);
    }
    setExpandedYears(newExpanded);
  };

  // Auto-expand current year
  React.useEffect(() => {
    const currentYear = new Date().getFullYear();
    setExpandedYears(new Set([currentYear]));
  }, []);

  if (isCollapsed) {
    return (
      <button
        onClick={onToggleCollapse}
        className="w-11 flex-shrink-0 self-stretch bg-medical-50 dark:bg-medical-800 border-r border-medical-200 dark:border-medical-700 flex flex-col items-center pt-4 gap-3 cursor-pointer hover:bg-medical-100 dark:hover:bg-medical-700 transition-colors group"
        title="Show timeline navigation"
      >
        <ChevronRight className="h-4 w-4 text-medical-500 group-hover:text-medical-700 dark:text-medical-400 dark:group-hover:text-medical-200 flex-shrink-0" />
        <span
          className="text-xs font-semibold text-medical-500 group-hover:text-medical-700 dark:text-medical-400 dark:group-hover:text-medical-200 uppercase tracking-wider flex items-center gap-2"
          style={{ writingMode: 'vertical-rl' }}
        >
          <Calendar className="h-4 w-4 flex-shrink-0" />
          Quick Jump
        </span>
      </button>
    );
  }

  return (
    <div className="w-64 bg-white dark:bg-medical-900 border-r border-medical-200 dark:border-medical-700 h-full overflow-y-auto flex-shrink-0">
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-medical-900 border-b border-medical-200 dark:border-medical-700 p-4 flex items-center justify-between z-10">
        <h3 className="font-semibold text-medical-900 dark:text-medical-100 flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Quick Jump
        </h3>
        <button
          onClick={onToggleCollapse}
          className="p-1 hover:bg-medical-50 dark:hover:bg-medical-800 rounded transition-colors group"
          title="Hide navigation"
        >
          <ChevronRight className="h-4 w-4 text-medical-400 group-hover:text-medical-600 dark:group-hover:text-medical-300 transform rotate-180" />
        </button>
      </div>

      {/* Timeline Navigation */}
      <div className="p-4 space-y-2">
        <div className="text-xs font-medium text-medical-500 dark:text-medical-400 uppercase tracking-wide mb-3">
          Timeline
        </div>

        {!isDateSort && (
          <div className="px-3 py-2 rounded-lg bg-medical-50 dark:bg-medical-800 text-xs text-medical-500 dark:text-medical-400">
            Sorted by {sortLabel?.toLowerCase() || 'custom order'} — month navigation unavailable
          </div>
        )}

        {isDateSort && yearGroups.map((group) => {
          const { year, yearEnd, months, totalCount, isRange } = group;
          const displayLabel = isRange && yearEnd ? `${year} – ${yearEnd}` : String(year);
          const isExpanded = expandedYears.has(year);
          const isCurrentYear = !isRange && year === new Date().getFullYear();

          return (
            <div key={isRange ? `${year}-${yearEnd}` : year}>
              {/* Year / Range Header */}
              <button
                onClick={() => toggleYear(year)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                  isCurrentYear
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-900 dark:text-primary-500 font-medium'
                    : 'hover:bg-medical-50 dark:hover:bg-medical-800 text-medical-700 dark:text-medical-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown className="h-3.5 w-3.5 text-medical-400" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 text-medical-400" />
                  )}
                  <span className="font-medium">{displayLabel}</span>
                </div>
                <span className="text-xs bg-medical-100 dark:bg-medical-700 text-medical-600 dark:text-medical-300 px-2 py-0.5 rounded-full">
                  {totalCount}
                </span>
              </button>

              {/* Months */}
              {isExpanded && (
                <div className="ml-6 mt-1 space-y-1">
                  {months.map(({ year: mYear, month, count, label }) => {
                    const isCurrentMonth =
                      mYear === new Date().getFullYear() &&
                      month === new Date().getMonth();

                    return (
                      <button
                        key={`${mYear}-${month}`}
                        onClick={() => onJumpToMonth(mYear, month)}
                        className={`w-full flex items-center justify-between px-3 py-1.5 rounded text-sm transition-colors ${
                          isCurrentMonth
                            ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-900 dark:text-primary-500 font-medium'
                            : 'hover:bg-medical-50 dark:hover:bg-medical-800 text-medical-600 dark:text-medical-400'
                        }`}
                      >
                        <span className="flex-1 text-left">{label}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-medical-100 dark:bg-medical-700 text-medical-600 dark:text-medical-300 font-medium">
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Detail Level (Significance) Filter */}
      {significanceLevels && significanceLevels.length > 0 && onSignificanceChange && (
        <div className="p-4 border-t border-medical-200 dark:border-medical-700">
          <div className="text-xs font-medium text-medical-500 dark:text-medical-400 uppercase tracking-wide mb-3">
            Detail Level
          </div>
          <div className="space-y-1">
            {significanceLevels.map(level => {
              const dotColor = level.key === 'MAJOR'
                ? 'bg-red-500'
                : level.key === 'SUPPORTING'
                  ? 'bg-amber-400'
                  : 'bg-gray-300 dark:bg-gray-600';
              return (
                <label
                  key={level.key}
                  className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg hover:bg-medical-50 dark:hover:bg-medical-800 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={level.enabled}
                    onChange={(e) => onSignificanceChange(level.key, e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-medical-300 dark:border-medical-600 text-primary-600 focus:ring-primary-500"
                  />
                  <div className={`w-2 h-2 rounded-full ${dotColor} flex-shrink-0`} />
                  <span className="flex-1 text-sm text-medical-700 dark:text-medical-300">
                    {level.label}
                  </span>
                  <span className="text-xs text-medical-400 tabular-nums">
                    {level.count}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Event Type Filters */}
      {eventTypeFilters.length > 0 && (
        <div className="p-4 border-t border-medical-200 dark:border-medical-700">
          <div className="text-xs font-medium text-medical-500 dark:text-medical-400 uppercase tracking-wide mb-3">
            Filter by Type
          </div>

          <div className="space-y-1">
            {eventTypeFilters.map(filter => (
              <label
                key={filter.type}
                className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg hover:bg-medical-50 dark:hover:bg-medical-800 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={filter.enabled}
                  onChange={(e) => onFilterChange(filter.type, e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-medical-300 dark:border-medical-600 text-primary-600 focus:ring-primary-500"
                />
                <span className="flex-1 text-sm text-medical-700 dark:text-medical-300">
                  {filter.label}
                </span>
                <span className="text-xs text-medical-400 tabular-nums">
                  {filter.count}
                </span>
              </label>
            ))}
          </div>

          {/* Clear Filters */}
          {eventTypeFilters.some(f => !f.enabled) && (
            <button
              onClick={() => {
                eventTypeFilters.forEach(f => {
                  if (!f.enabled) onFilterChange(f.type, true);
                });
              }}
              className="w-full mt-3 text-xs text-primary-600 dark:text-primary-500 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
            >
              Clear All Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};
